// @flow
import { useState } from 'react';

import capitalize from 'lodash/capitalize';
import { Info, Percent, Repeat } from 'lucide-react';

import { useDispatch } from 'react-redux';

import {
  updateCartProduct,
  getCart,
  type DecoratedProduct,
  type ProductError,
} from '@gandi/cart';
import { useTranslator } from '@gandi/translate';

import { Badge, Base, Button } from '@gandi/ui';

import { useSimplehostingPrices } from 'src/WebHosting/modules/prices';
import { useNotifications } from 'src/hooks';
import type { InstanceSize } from 'src/modules/simplehosting/prices';

import { ListRow } from '../../ListRow';

import { PlanDisplay } from './components/Rows/PlanContent';
import {
  useWebhostingDiffPrices,
  useConfigurationDetailsUpSell,
  useWebhostingPlanPrices,
} from './components/modules/webhosting';

import styles from './WebHostingSizeError.scss';

type Props = {
  error?: ProductError,
  size?: InstanceSize,
  product: DecoratedProduct,
};

export function WebHostingSizeError({
  error,
  size,
  product,
}: Props): React.MixedElement | null {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const [isUpdating, setIsUpdating] = useState(false);

  const { notifyMaybeApiError } = useNotifications();

  const { summaries: instancesPrices, isLoaded: instancesPricesLoaded } =
    useSimplehostingPrices();

  async function handleCLick() {
    setIsUpdating(true);
    try {
      await dispatch(
        updateCartProduct({
          productId: product.id,
          payload: { ...product.payload, size: 'starter' },
        }),
      );

      await dispatch(getCart());
    } catch (e) {
      notifyMaybeApiError(e);
    }

    setIsUpdating(false);
  }

  const diffMonthly = useWebhostingDiffPrices('starter');

  const { cpu, storage } =
    useConfigurationDetailsUpSell({
      duration: 1,
      size: !size || size === 'essential' ? 'starter' : size,
    }) || {};

  const { firstPrice, pricesByPlanByDuration, firstPriceCrossedOutForWHSE } =
    useWebhostingPlanPrices(product?.payload.duration || 1, [
      {
        value: 'starter',
        text: __('Starter'),
      },
    ]) || {};

  if (!instancesPricesLoaded || !instancesPrices?.length || !error || !size)
    return null;

  const freeMonths = {
    id: 'freeMonths',
    component: (
      <li key="freeMonths" className={styles.item}>
        <Percent size={16} />
        <span>{diffMonthly}</span>
      </li>
    ),
  };

  const options = [
    { id: 'storage', content: storage },
    { id: 'cpu', content: cpu },
  ];
  const sizeLabel = size === 'essential' ? __('Essential') : capitalize(size);

  return (
    <>
      <PlanDisplay disabled sizeLabel={sizeLabel} options={options} />

      <ListRow icon={<Info size={20} />} intent="danger-strong">
        {error.description} {__('Please select an offer below to continue.')}
      </ListRow>

      <PlanDisplay
        sizeLabel={
          <Base flex gap="spacing-4x" flexAlignItemsCenter>
            {capitalize('starter')}
            <Badge>
              {__('%(duration)s billing', {
                duration: __('Yearly'),
              })}
            </Badge>
          </Base>
        }
        options={[freeMonths, ...options]}
        price={firstPrice}
        priceCrossOut={firstPriceCrossedOutForWHSE}
        durationWording={__(
          '<for-translators-use>Periodicity (month)</for-translators-use>mo',
        )}
        detail={
          product.payload.duration !== 12 &&
          pricesByPlanByDuration && (
            <>
              <span>
                {__('Pay yearly and save %(diffYearly)s', {
                  diffYearly: pricesByPlanByDuration.starter,
                })}
              </span>
              <Repeat size={12} />
            </>
          )
        }
        cta={
          <Base flex gap="spacing-4x">
            <Button to="/simplehosting" kind="link">
              {__('View all offers')}
            </Button>
            <Button
              small
              type="button"
              loading={isUpdating}
              onClick={handleCLick}
            >
              {__('Add to cart')}
            </Button>
          </Base>
        }
      />
    </>
  );
}
